import { EventEmitter, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NotImplementedError } from 'src/app/utils/error';
import { LoggerService } from '../logger/logger.service';
import { IQueryContext, IQueryContextChange } from './models/IQueryContext';
import { sortComparatorDatasourceColumnIds } from 'src/app/utils/datasource-sql-map/datasource';
import { QueryContextHelper } from './query-context-helper';

export type QueryContextType = 'Pending' | 'Applied';
export type QueryContextChangeReason =
    | 'InitialContext'
    | 'NewContextChange'
    | 'AppliedPendingContext';
/** Change of query context */
export type QueryContextChange = {
    reason: QueryContextChangeReason;
    context: IQueryContext;
};

@Injectable({
    providedIn: 'root',
})
export class QueryContextService {
    public pendingQueryContextEmitter: EventEmitter<QueryContextChange> =
        new EventEmitter();
    public appliedQueryContextEmitter: EventEmitter<QueryContextChange> =
        new EventEmitter();

    private pendingQueryContext: IQueryContext = QueryContextService.EMPTY;
    private appliedQueryContext: IQueryContext = QueryContextService.EMPTY;

    constructor(
        private loggerService: LoggerService,
        private router: Router,
        public activatedRoute: ActivatedRoute,
        private notification: NzNotificationService
    ) {}

    initializeContext(queryContext: IQueryContext) {
        // this.loggerService.debug(
        //     `[QueryContextService.initializeContexts] pendingQueryContexts and appliedQueryContext initialized.`,
        //     queryContext,
        // );
        QueryContextHelper.normalizeDatasourceColumnState(queryContext);
        // sort datasourceColumns
        queryContext.datasourceColumns.sort((a, b) =>
            sortComparatorDatasourceColumnIds(a.id, b.id)
        );

        this.pendingQueryContext = structuredClone(queryContext);
        this.appliedQueryContext = structuredClone(queryContext);
        this.appliedQueryContextEmitter.emit({
            reason: 'InitialContext',
            context: this.appliedQueryContext,
        });
    }

    // Components can call this function.
    // What about hazard?
    // Add new parameter with hash of previous context
    // Compare and if different than ignore / log error
    // return true if ok
    // return false / object with details if error
    private setQueryContext(
        reason: QueryContextChangeReason,
        queryContext: IQueryContext,
        type: 'pending' | 'applied' = 'pending'
    ) {
        console.log({
            msg: `[QueryContextService.setQueryContext] ${type}QueryContext changed. Reason: ${reason}.`,
            queryContext,
        });

        // sort datasourceColumns
        queryContext.datasourceColumns.sort((a, b) =>
            sortComparatorDatasourceColumnIds(a.id, b.id)
        );

        switch (type) {
            case 'pending':
                this.pendingQueryContext = structuredClone(queryContext);
                console.log({
                    msg: `[QueryContextService.setQueryContext] pendingQueryContext`,
                    queryContext,
                    thispendingQueryContext: this.pendingQueryContext,
                    thispendingQueryContextString: JSON.stringify(
                        this.pendingQueryContext
                    ),
                });
                this.pendingQueryContextEmitter.emit({
                    reason,
                    context: structuredClone(queryContext),
                });
                break;
            case 'applied':
                this.appliedQueryContext = structuredClone(queryContext);
                this.appliedQueryContextEmitter.emit({
                    reason,
                    context: structuredClone(queryContext),
                });
                break;
            default:
                throw new NotImplementedError();
                break;
        }
    }

    applyPendingQueryContext() {
        this.appliedQueryContext = structuredClone(this.pendingQueryContext);
        console.log({
            msg: '[QueryContextService.applyPendingQueryContext] appliedQueryContext changed.',
            appliedQueryContext: this.appliedQueryContext,
        });
        this.appliedQueryContextEmitter.emit(
            structuredClone({
                reason: 'AppliedPendingContext',
                context: this.appliedQueryContext,
            })
        );
    }

    changePendingQueryContext(contextChange: IQueryContextChange) {
        // clone pendingQueryContext so other object referencing this won't be affected
        const pendingQueryContext: IQueryContext = structuredClone(
            this.pendingQueryContext
        );

        console.log({
            msg: 'QueryContextService.changePendingQueryContext',
            contextChange,
            thispendingQueryContext: this.pendingQueryContext,
            localpendingQueryContextString: JSON.stringify(pendingQueryContext),
            thispendingQueryContextString: JSON.stringify(
                this.pendingQueryContext
            ),
        });
        if (contextChange.setting) {
            console.log({
                msg: 'QueryContextService.changePendingQueryContext.settings',
                contextChange,
            });
            // remove from pending if exists
            if (contextChange.setting!.id in pendingQueryContext.settings) {
                console.log({
                    msg: 'QueryContextService.changePendingQueryContext.settings deleted existing setting',
                });
                delete pendingQueryContext.settings[contextChange.setting.id];
            }
            // TODO isActive
            // TODO typing
            // if value is not null or undefined then update pendingQueryContext.settings
            if (
                'value' in contextChange.setting &&
                contextChange.setting.value
            ) {
                console.log({
                    msg: `QueryContextService.changePendingQueryContext.settings updated settings ${contextChange.setting.value}`,
                });
                (pendingQueryContext.settings as any)[
                    contextChange.setting.id
                ] = contextChange.setting.value;
            }
            console.log({
                msg: 'QueryContextService.changePendingQueryContext.settings',
                localpendingQueryContextString:
                    JSON.stringify(pendingQueryContext),
                thispendingQueryContextString: JSON.stringify(
                    this.pendingQueryContext
                ),
            });
        }

        if (contextChange.datasourceColumn) {
            const datasourceColumn = structuredClone(
                contextChange.datasourceColumn
            );
            console.log({
                msg: 'QueryContextService.changePendingQueryContext.datasourceColumn',
                contextChange,
                pendingContext: pendingQueryContext,
            });
            // remove from pending if exists
            pendingQueryContext.datasourceColumns =
                pendingQueryContext.datasourceColumns?.filter((pending) => {
                    return pending.id != datasourceColumn.id;
                });
            console.log({
                msg: 'QueryContextService.changePendingQueryContext.datasourceColumn',
                pendingQueryContext,
            });

            // if filter, split or bucket applied
            if (
                datasourceColumn.state.isFilterApplied ||
                datasourceColumn.state.isSplitApplied ||
                datasourceColumn.state.isBucketApplied
            ) {
                pendingQueryContext.datasourceColumns.push(datasourceColumn);
            }
        }
        QueryContextHelper.normalizeDatasourceColumnState(
            pendingQueryContext,
            contextChange.datasourceColumn
        );
        this.setQueryContext('NewContextChange', pendingQueryContext);
    }

    public static get INITIAL_CONTEXT(): IQueryContext {
        return structuredClone({
            settings: {
                showTotalRevenue: true,
                currency: 'pln',
                netGross: 'net',
                retentionSelectedDayNo: [3, 7, 30],
            },
            datasourceColumns: [],
            // filters: [],
            // splits: [],
        });
    }

    /** Returns empty objects of IQueryContext */
    public static get EMPTY(): IQueryContext {
        return structuredClone({
            settings: {},
            datasourceColumns: [],
            // splits: [],
            // filters: [],
        });
    }

    public getAppliedQueryContext(): IQueryContext {
        return structuredClone(this.appliedQueryContext);
    }
}
